import {useEffect, useRef, useState} from 'react';

import {clearUserAboutMe, getUserAboutMe} from 'llm/chat-library/sidepanel/OnboardingDAO';

interface UseUserAboutMeResult {
  aboutMe: string | null;
  isLoading: boolean;
  clearAboutMe: () => Promise<void>;
}

interface UseUserAboutMeOptions {
  enabled?: boolean;
}

/**
 * Hook to manage the user's onboarding-generated About Me paragraph.
 * Pass `enabled: false` to skip fetching (e.g. when LLM_ENABLE_AI_ONBOARDING is off).
 */
export default function useUserAboutMe({enabled = true}: UseUserAboutMeOptions = {}): UseUserAboutMeResult {
  const [aboutMe, setAboutMe] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(enabled);
  const isMountedRef = useRef(true);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    isMountedRef.current = true;

    async function loadAboutMe(): Promise<void> {
      const existing = await getUserAboutMe();
      if (isMountedRef.current) {
        setAboutMe(existing);
        setIsLoading(false);
      }
    }

    loadAboutMe();

    return () => {
      isMountedRef.current = false;
    };
  }, [enabled]);

  async function clearAboutMe(): Promise<void> {
    await clearUserAboutMe();
    if (isMountedRef.current) {
      setAboutMe(null);
    }
  }

  return {aboutMe, isLoading, clearAboutMe};
}
